import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useActionItems, type ActionItem, type ActionPriority } from "@/lib/hooks";
import { fmtDate } from "@/lib/format";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";

const PRIORITY_LABEL: Record<ActionPriority, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

const PRIORITY_CLASSES: Record<ActionPriority, string> = {
  high: "bg-red-50 text-danger",
  medium: "bg-amber-50 text-warning",
  low: "bg-ink-100 text-ink-600",
};

const TYPE_LABEL: Record<string, string> = {
  win_back: "Win-back",
  account_check_in: "Check-in",
  silent_with_monitors: "Silent",
  onboarding: "Onboarding",
  refresh_training: "Refresh training",
  underutilized: "Underutilized",
  epic_opportunity: "Epic opportunity",
};

export default function Actions() {
  const { data, isLoading, error } = useActionItems();
  const [priorityFilter, setPriorityFilter] = useState<ActionPriority | "all">("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const filtered = useMemo(() => {
    if (!data) return [] as ActionItem[];
    return data.action_items.filter((a) => {
      if (priorityFilter !== "all" && a.priority !== priorityFilter) return false;
      if (typeFilter !== "all" && a.type !== typeFilter) return false;
      return true;
    });
  }, [data, priorityFilter, typeFilter]);

  const distinctTypes = useMemo(() => {
    if (!data) return [] as string[];
    return Array.from(new Set(data.action_items.map((a) => a.type))).sort();
  }, [data]);

  function exportXls() {
    downloadXlsx(
      stampedName("action-items"),
      ["Priority", "Customer", "Action", "Detail", "Type"],
      filtered.map((a) => [
        PRIORITY_LABEL[a.priority], a.customer_name, a.title, a.detail, TYPE_LABEL[a.type] ?? a.type,
      ]),
    );
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Action items</h1>
          <p className="mt-1 text-sm text-ink-500">
            Recommended next steps for the sales &amp; clinical team, derived from order
            patterns and customer profile data.
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <PriorityChip label="High"   value={data.counts_by_priority.high}   color="text-danger" />
            <PriorityChip label="Medium" value={data.counts_by_priority.medium} color="text-warning" />
            <PriorityChip label="Low"    value={data.counts_by_priority.low}    color="text-ink-600" />
            <span>· Generated {fmtDate(data.generated_at)}</span>
          </div>
        )}
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">{(error as Error).message}</div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-ink-500">
          Priority
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as ActionPriority | "all")}
            className="rounded-md border border-ink-200 px-2 py-1 text-xs text-ink-900"
          >
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-ink-500">
          Type
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-md border border-ink-200 px-2 py-1 text-xs text-ink-900"
          >
            <option value="all">All types</option>
            {distinctTypes.map((t) => (
              <option key={t} value={t}>{TYPE_LABEL[t] ?? t}</option>
            ))}
          </select>
        </label>
        <div className="ml-auto">
          <DownloadButton onClick={exportXls} disabled={!filtered.length} />
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-10 text-center text-sm text-ink-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-ink-500">
              Nothing to do — every customer looks healthy on the active filters.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-5 py-2 text-left">Priority</th>
                  <th className="px-5 py-2 text-left">Customer</th>
                  <th className="px-5 py-2 text-left">Action</th>
                  <th className="px-5 py-2 text-left">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {filtered.map((a) => (
                  <tr key={a.id} className="hover:bg-ink-50">
                    <td className="px-5 py-3 align-top">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_CLASSES[a.priority]}`}>
                        {PRIORITY_LABEL[a.priority]}
                      </span>
                    </td>
                    <td className="px-5 py-3 align-top">
                      <Link
                        to={`/customers/${encodeURIComponent(a.customer_id)}`}
                        className="font-medium text-ink-900 hover:text-accent-600"
                      >
                        {a.customer_name}
                      </Link>
                    </td>
                    <td className="px-5 py-3 align-top text-ink-900">{a.title}</td>
                    <td className="px-5 py-3 align-top text-ink-600">{a.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function PriorityChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`tabular-nums font-medium ${color}`}>{value}</span>
      <span>{label}</span>
    </span>
  );
}
